'use client';

import { useEffect, useRef, useState } from 'react';

interface GoogleReCaptchaV2Props {
  action: string;
}

type ReCaptchaEnterprise = {
  ready: (callback: () => void) => void;
  render: (container: HTMLElement, parameters: Record<string, unknown>) => number;
};

const MAX_RETRIES = 3;
const TOKEN_COOKIE_TTL_MS = 60 * 1000;

// render=explicit desliga o render automático dos elementos com a classe g-recaptcha. Sem isso o
// widget aparece duas vezes: uma pelo automático, no load do script, e outra pelo render() daqui —
// e na segunda montagem do componente o render() bate em cima do automático.
const SCRIPT_SRC = 'https://www.google.com/recaptcha/enterprise.js?render=explicit';

let scriptPromise: Promise<void> | null = null;

function getEnterprise(): ReCaptchaEnterprise | undefined {
  return (window as unknown as { grecaptcha?: { enterprise?: ReCaptchaEnterprise } }).grecaptcha?.enterprise;
}

function loadRecaptchaScript(): Promise<void> {
  if (typeof window === 'undefined') return Promise.reject(new Error('reCAPTCHA requires a browser'));
  if (getEnterprise()) return Promise.resolve();

  if (!scriptPromise) {
    scriptPromise = new Promise<void>((resolve, reject) => {
      const existing = document.querySelector<HTMLScriptElement>('script[src*="recaptcha/enterprise.js"]');
      const script = existing ?? document.createElement('script');

      script.addEventListener('load', () => resolve());
      script.addEventListener('error', () => reject(new Error('Failed to load reCAPTCHA script')));

      if (!existing) {
        script.src = SCRIPT_SRC;
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      }
    }).catch((error) => {
      scriptPromise = null;
      throw error;
    });
  }

  return scriptPromise;
}

// O load do script só expõe o carregador; render() aparece depois, quando o ready() dispara.
function whenReady(): Promise<ReCaptchaEnterprise> {
  return new Promise((resolve, reject) => {
    const enterprise = getEnterprise();
    if (!enterprise) return reject(new Error('reCAPTCHA enterprise unavailable after load'));
    enterprise.ready(() => resolve(enterprise));
  });
}

function saveToken(token: string) {
  document.cookie = `_grt=${token};expires=${new Date(Date.now() + TOKEN_COOKIE_TTL_MS).toUTCString()};path=/`;
}

export const GoogleReCaptchaV2 = ({ action }: GoogleReCaptchaV2Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetId = useRef<number | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    let cancelled = false;

    loadRecaptchaScript()
      .then(whenReady)
      .then((enterprise) => {
        if (cancelled || widgetId.current !== null || !containerRef.current) return;

        widgetId.current = enterprise.render(containerRef.current, {
          sitekey: process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY,
          action,
          callback: saveToken,
        });
      })
      .catch((error) => {
        if (cancelled) return;

        if (retryCount < MAX_RETRIES) {
          setTimeout(() => setRetryCount((previous) => previous + 1), 1000);
          return;
        }

        console.error('Error rendering reCAPTCHA:', error);
      });

    return () => {
      cancelled = true;
    };
  }, [action, retryCount]);

  return <div ref={containerRef} id='g-recaptcha' />;
};
